import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { ObjectChecklist } from "../../../../api/types/objectTypes";

const deleteChecklist = withErrorHandling(adminApi.deleteObjectChecklist);

interface Props {
  objectType: string;
  checklist: ObjectChecklist;
  open: boolean;
  onClose: () => void;
  onDeleted: (updatedChecklists: ObjectChecklist[]) => void;
}

const DeleteChecklistDialog = ({ objectType, checklist, open, onClose, onDeleted }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    const [updatedChecklists, error] = await deleteChecklist(objectType, checklist.id);
    setLoading(false);

    if (error) {
      logError(error, "[DeleteChecklistDialog]");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Checklist deleted successfully");
    onDeleted(updatedChecklists);
  };

  return (
    <SmallDialog open={open} onClose={onClose}>
      <DialogTitle>Delete</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="h6">Delete checklist?</Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              You&apos;re about to delete <strong>{checklist.name}</strong>. This action is permanent and can&apos;t be
              undone.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="outlined" color="error" loading={loading} onClick={handleSubmit}>
          Delete
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteChecklistDialog;
